import React, { useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';

const TermsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const lastUpdated = "January 24, 2026";

  // Scroll to section if hash is present
  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.slice(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location.hash]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const sections = [
    { id: 'summary', title: 'Non-binding summary' },
    { id: 'scope', title: '1. Scope and acceptance' },
    { id: 'definitions', title: '2. Definitions' },
    { id: 'role', title: '3. Role of SnowExchange' },
    { id: 'no-employment', title: '4. No employment, agency, or representation' },
    { id: 'accounts', title: '5. Accounts and access' },
    { id: 'user-content', title: '6. User content and visibility' },
    { id: 'data-accuracy', title: '7. Data accuracy, legal existence & due diligence' },
    { id: 'contracts', title: '8. Contracts, billing and payments' },
    { id: 'templates', title: '9. Templates and information "as is"' },
    { id: 'prohibited', title: '10. Prohibited use' },
    { id: 'suspension', title: '11. Suspension and termination' },
    { id: 'disclaimer', title: '12. Disclaimer of warranties' },
    { id: 'liability', title: '13. Limitation of liability' },
    { id: 'indemnification', title: '14. Indemnification' },
    { id: 'data-protection', title: '15. Data protection' },
    { id: 'ip', title: '16. Intellectual property' },
    { id: 'third-party', title: '17. Third-party services' },
    { id: 'governing-law', title: '18. Governing law and jurisdiction' },
    { id: 'changes', title: '19. Changes to the Terms' },
    { id: 'contact', title: '20. Contact' },
    { id: 'annex-a', title: 'Annex A – Public Sector, Security & Residency Constraints' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate('/')} className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Back to Home</span>
          </Button>
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-gray-800">Terms of Use</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 py-8 md:py-12">
        <div className="lg:flex lg:gap-8">
          {/* Sidebar Table of Contents - Desktop */}
          <aside className="hidden lg:block lg:w-72 flex-shrink-0">
            <div className="sticky top-24 bg-white rounded-xl shadow-sm border border-gray-200 p-4 max-h-[calc(100vh-120px)] overflow-y-auto">
              <h3 className="font-semibold text-gray-800 mb-4 text-sm uppercase tracking-wide">Table of Contents</h3>
              <nav className="space-y-1">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => scrollToSection(section.id)}
                    className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    {section.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 p-6 md:p-10">
            {/* Title */}
            <div className="text-center mb-10 pb-8 border-b border-gray-200">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">Terms of Use & Limitation of Liability</h1>
              <p className="text-gray-500">Last updated: {lastUpdated}</p>
            </div>

            {/* Mobile Table of Contents */}
            <div className="lg:hidden mb-8">
              <details className="bg-gray-50 rounded-lg border border-gray-200">
                <summary className="px-4 py-3 cursor-pointer font-semibold text-gray-800">
                  Table of Contents
                </summary>
                <nav className="px-4 pb-4 space-y-1">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    >
                      {section.title}
                    </button>
                  ))}
                </nav>
              </details>
            </div>

            {/* Non-binding summary */}
            <section id="summary" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Non-binding summary</h2>
              <div className="bg-amber-50 border-l-4 border-amber-400 p-5 rounded-r-lg">
                <p className="text-gray-700 leading-relaxed">
                  SnowExchange is a B2B matchmaking and intermediation platform providing tools only, with no obligation of result. All contracts, services, payments, deliverables, delays, disputes, and project outcomes are the exclusive responsibility of the contracting parties. SnowExchange does not verify the accuracy, legality, or validity of information provided by users (including legal existence, certifications, or capabilities). Billing and payments take place outside the platform, unless explicitly agreed otherwise in writing. SnowExchange disclaims liability for indirect damages, business losses, non-payment, or project data issues, to the maximum extent permitted by law.
                </p>
                <p className="text-sm text-amber-700 mt-4 font-medium">
                  This summary is provided for convenience only. The legally binding terms are set out below.
                </p>
              </div>
            </section>

            {/* 1. Scope and acceptance */}
            <section id="scope" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Scope and acceptance</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  SnowExchange is currently operated by a legally registered business entity. The operating entity may change over time, including as part of incorporation, restructuring, or group formation, without affecting the validity or enforceability of these Terms.
                </p>
                <p>By accessing or using the Service, you confirm that you:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>act in a professional / business capacity,</li>
                  <li>have authority to bind the entity you represent (if applicable),</li>
                  <li>accept and agree to be bound by these Terms.</li>
                </ul>
                <p className="font-medium text-gray-900">
                  If you do not agree, you must not use the Service.
                </p>
              </div>
            </section>

            {/* 2. Definitions */}
            <section id="definitions" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Definitions</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200 space-y-4">
                  <div>
                    <p className="font-semibold text-gray-800">"Platform" / "Service"</p>
                    <p className="text-sm">the SnowExchange platform and its functionalities (search, matching, messaging, profiles, etc.).</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">"User"</p>
                    <p className="text-sm">any individual or legal entity using the Platform.</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">"PM" (Project Manager)</p>
                    <p className="text-sm">a user or company seeking resources or delivery capacity.</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">"RM" (Resource Manager)</p>
                    <p className="text-sm">a user or company offering resources, consultants, freelancers, or delivery capacity.</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">"Content"</p>
                    <p className="text-sm">any data, text, documents, profiles, messages, or information shared via the Platform.</p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <p className="font-semibold text-blue-800">"SnowExchange Parties"</p>
                    <p className="text-sm text-blue-700">means: (i) the legal entity currently operating the SnowExchange platform, as identified in the Legal Notice or equivalent section of the Platform, (ii) its affiliates (if any), (iii) its directors, officers, employees, contractors and service providers, and (iv) its commercial partners, including, where applicable, SCS Schnittker Consulting LLC.</p>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">"User Contracts"</p>
                    <p className="text-sm">any contract entered into directly between Users.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* 3. Role of SnowExchange */}
            <section id="role" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Role of SnowExchange</h2>
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <div>
                  <h3 className="font-medium text-gray-800 mb-3">3.1 Intermediation platform – obligation of means</h3>
                  <p className="mb-3">
                    SnowExchange operates solely as a B2B intermediation and matchmaking platform. It provides tools enabling Users, for convenience only and without assuming any responsibility, to:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>discover and identify potential business partners;</li>
                    <li>present needs, capabilities, and availability;</li>
                    <li>communicate, exchange documents, and organise discussions or interviews;</li>
                    <li>access non-mandatory contract templates provided on an "as is" basis;</li>
                    <li>collaborate around the preparation, execution, and follow-up of contracts concluded directly between Users, including, where applicable, electronic signature functionalities;</li>
                    <li>monitor the progress of collaborations and provide post-collaboration feedback or ratings.</li>
                  </ul>
                  <p className="mt-4">
                    All such tools are provided as support functionalities only. Their availability or use does not imply any validation, supervision, endorsement, certification, or guarantee by SnowExchange of:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 mt-2">
                    <li>the contractual terms agreed between Users,</li>
                    <li>the execution, quality, or outcome of any services,</li>
                    <li>the accuracy or fairness of ratings or feedback, or</li>
                    <li>the suitability, reliability, or compliance of any User.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-medium text-gray-800 mb-3">3.2 No involvement in service execution</h3>
                  <p>SnowExchange does not perform, supervise, or control the services provided by Users and bears no responsibility for:</p>
                  <ul className="list-disc pl-6 space-y-2 mt-2">
                    <li>quality, performance, compliance, or availability,</li>
                    <li>on-site presence or delivery timelines,</li>
                    <li>project outcomes or deliverables.</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-medium text-gray-800 mb-3">3.3 Ratings, feedback and evaluations</h3>
                  <p className="mb-3">
                    The Platform may allow Users to provide ratings, reviews, feedback, or qualitative evaluations regarding past collaborations, including indicators generated or assisted by automated or AI-based tools.
                  </p>
                  <p className="mb-2">Users acknowledge and agree that:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>all ratings, reviews, and feedback primarily reflect subjective opinions and experiences of Users;</li>
                    <li>such evaluations are provided for informational purposes only and do not constitute endorsements, guarantees, or certifications by SnowExchange;</li>
                    <li>SnowExchange does not verify the accuracy, completeness, fairness, or objectivity of ratings or feedback.</li>
                  </ul>
                  <p className="mt-4 mb-2">Where qualitative indicators or summaries are generated or assisted by automated or AI-based tools:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>they are based on User-provided information and interaction data;</li>
                    <li>they do not constitute automated decision-making with legal or contractual effect;</li>
                    <li>they do not replace independent human assessment or due diligence.</li>
                  </ul>
                  <p className="mt-4 mb-2">SnowExchange Parties shall not be liable for:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>decisions made by Users based on ratings, feedback, or evaluations;</li>
                    <li>reputational, commercial, or contractual consequences arising from such information;</li>
                    <li>disputes between Users relating to ratings or reviews.</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 4. No employment, agency, or representation */}
            <section id="no-employment" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">4. No employment, agency, or representation</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Nothing in these Terms creates:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>an employment relationship,</li>
                  <li>an agency, brokerage, or representative relationship,</li>
                  <li>a partnership or joint venture,</li>
                </ul>
                <p>between SnowExchange and any User, or between Users themselves.</p>
                <p className="font-medium text-gray-900">Users act independently and at their own risk.</p>
              </div>
            </section>

            {/* 5. Accounts and access */}
            <section id="accounts" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Accounts and access</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Users are responsible for:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>safeguarding their login credentials,</li>
                  <li>all activity occurring under their account,</li>
                  <li>ensuring information remains accurate and up to date.</li>
                </ul>
              </div>
            </section>

            {/* 6. User content and visibility */}
            <section id="user-content" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">6. User content and visibility</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Users retain ownership of their Content but grant SnowExchange Parties a non-exclusive, worldwide, royalty-free license to host, process, display, and share such Content solely for operating and improving the Platform, subject to visibility settings. Users represent and warrant that their Content is lawful, accurate, and does not infringe third-party rights.
                </p>
              </div>
            </section>

            {/* 7. Data accuracy, legal existence & due diligence (KEY CLAUSE) */}
            <section id="data-accuracy" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                7. Data accuracy, legal existence & due diligence
                <span className="ml-2 px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded uppercase">Key Clause</span>
              </h2>
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <div>
                  <h3 className="font-medium text-gray-800 mb-3">7.1 No verification by SnowExchange</h3>
                  <p className="mb-2">Users expressly acknowledge and agree that SnowExchange:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>does not verify the identity, legal existence, solvency, authority, or capacity of Users,</li>
                    <li>does not validate certifications, skills, experience, availability, rates, or qualifications,</li>
                    <li>does not guarantee the accuracy, completeness, or currency of any information displayed on the Platform.</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium text-gray-800 mb-3">7.2 User responsibility</h3>
                  <p className="mb-2">Users are solely responsible for conducting their own due diligence prior to entering into any User Contract, including but not limited to:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>legal existence and registration checks,</li>
                    <li>authority to contract,</li>
                    <li>regulatory, tax, employment, and social compliance,</li>
                    <li>security clearances, permits, visas, or work authorisations,</li>
                    <li>technical and operational suitability.</li>
                  </ul>
                  <p className="mt-4 font-medium text-gray-900">
                    Any decision to collaborate based on information available on SnowExchange is made at the User's own risk.
                  </p>
                </div>
              </div>
            </section>

            {/* 8. Contracts, billing and payments */}
            <section id="contracts" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Contracts, billing and payments</h2>
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <div>
                  <h3 className="font-medium text-gray-800 mb-3">8.1 User contracts</h3>
                  <p className="mb-2">Users may use templates provided on the Platform or their own contracts. In all cases:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>contracts are concluded directly between Users,</li>
                    <li>SnowExchange is not a party to such contracts.</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium text-gray-800 mb-3">8.2 Payments</h3>
                  <p className="mb-2">Unless explicitly agreed otherwise in a separate written agreement:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>SnowExchange does not host billing or payments,</li>
                    <li>does not collect or process payments between Users,</li>
                    <li>is not responsible for advances, instalments, refunds, penalties, disputes, or non-payment.</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 9. Templates and information "as is" */}
            <section id="templates" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Templates and information "as is"</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Any contract templates, sample clauses, guidance, collaboration tools, document-sharing features, electronic signature functionalities, or informational content made available through the Platform are provided strictly on an "as is" and "as available" basis, for convenience only. Such materials and tools:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>do not constitute legal, tax, HR, regulatory, or professional advice;</li>
                  <li>do not replace the Users' own contracts, legal review, or internal approval processes;</li>
                  <li>are not tailored to any specific jurisdiction, industry, or regulatory context.</li>
                </ul>
                <p>
                  SnowExchange does not draft, validate, review, negotiate, approve, or enforce User Contracts, even where Users rely on templates or execute contracts using Platform functionalities.
                </p>
                <p className="mb-2">Users remain solely responsible for:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>the content, validity, enforceability, and compliance of their contracts;</li>
                  <li>ensuring that contractual terms meet applicable legal, regulatory, and security requirements;</li>
                  <li>obtaining independent professional advice where necessary.</li>
                </ul>
              </div>
            </section>

            {/* 10. Prohibited use */}
            <section id="prohibited" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Prohibited use</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>Users must not:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>provide false or misleading information,</li>
                  <li>impersonate another person or entity,</li>
                  <li>violate confidentiality, IP, or data protection laws,</li>
                  <li>interfere with the Platform's security or operation,</li>
                  <li>use the Platform for unlawful purposes.</li>
                </ul>
              </div>
            </section>

            {/* 11. Suspension and termination */}
            <section id="suspension" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Suspension and termination</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="mb-2">SnowExchange may suspend or terminate accounts or restrict access where reasonably necessary to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>enforce these Terms,</li>
                  <li>prevent fraud, abuse, or security risks,</li>
                  <li>comply with legal obligations.</li>
                </ul>
                <p className="font-medium text-gray-900 mt-4">
                  SnowExchange shall not be liable for consequences of justified suspension or termination.
                </p>
              </div>
            </section>

            {/* 12. Disclaimer of warranties */}
            <section id="disclaimer" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Disclaimer of warranties</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <div className="bg-gray-100 border border-gray-300 rounded-lg p-5">
                  <p>
                    The Platform is provided "as is" and "as available", without warranties of any kind, including implied warranties of merchantability, fitness for a particular purpose, or uninterrupted availability.
                  </p>
                </div>
              </div>
            </section>

            {/* 13. Limitation of liability */}
            <section id="liability" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                13. Limitation of liability
                <span className="ml-2 px-2 py-1 bg-red-100 text-red-700 text-xs font-bold rounded uppercase">Critical</span>
              </h2>
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <div>
                  <h3 className="font-medium text-gray-800 mb-3">13.1 Exclusion of indirect damages</h3>
                  <p className="mb-2">To the maximum extent permitted by law, SnowExchange Parties shall not be liable for:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>loss of profits, revenue, or business opportunity,</li>
                    <li>reputational damage,</li>
                    <li>indirect, incidental, special, or consequential damages.</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium text-gray-800 mb-3">13.2 Explicit exclusions</h3>
                  <p className="mb-2">SnowExchange Parties are not liable for:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>failure or poor performance of User services,</li>
                    <li>absence, delay, or breach by a User,</li>
                    <li>disputes or non-payment between Users,</li>
                    <li>loss or disclosure of project data exchanged between Users,</li>
                    <li>decisions made based on Platform content or recommendations.</li>
                  </ul>
                </div>
                <div className="bg-amber-50 border border-amber-300 rounded-lg p-5">
                  <h3 className="font-medium text-amber-800 mb-3">13.3 Liability cap</h3>
                  <p className="text-amber-700 mb-2">Where liability cannot be excluded, SnowExchange Parties' total aggregate liability shall be limited to:</p>
                  <ul className="list-disc pl-6 space-y-2 text-amber-700">
                    <li>the fees actually paid to SnowExchange by the User during the <strong>three (3) months</strong> preceding the event, or</li>
                    <li><strong>CHF 100</strong> if no fees were paid.</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* 14. Indemnification */}
            <section id="indemnification" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">14. Indemnification</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p className="mb-2">Users agree to indemnify and hold harmless SnowExchange Parties from any claims, damages, or costs arising from:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>use of the Platform,</li>
                  <li>User Content,</li>
                  <li>violation of law or these Terms,</li>
                  <li>disputes between Users.</li>
                </ul>
              </div>
            </section>

            {/* 15. Data protection */}
            <section id="data-protection" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">15. Data protection</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Personal data processing is governed by the Privacy Policy available at <Link to="/privacy" className="text-blue-600 hover:underline">/privacy</Link>, which should be read together with these Terms and the <Link to="/ai-disclaimer" className="text-blue-600 hover:underline">AI Disclaimer</Link>.
                </p>
              </div>
            </section>

            {/* 16. Intellectual property */}
            <section id="ip" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">16. Intellectual property</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  All Platform elements (excluding User Content) are the property of SnowExchange Parties or their licensors and may not be used without permission.
                </p>
              </div>
            </section>

            {/* 17. Third-party services */}
            <section id="third-party" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">17. Third-party services</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  The Platform may rely on third-party services. SnowExchange is not responsible for their acts or omissions to the extent permitted by law.
                </p>
              </div>
            </section>

            {/* 18. Governing law and jurisdiction */}
            <section id="governing-law" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">18. Governing law and jurisdiction</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  These Terms are governed by the law applicable to the operating entity of SnowExchange, and disputes shall be subject to the competent courts thereof, unless mandatory law provides otherwise.
                </p>
              </div>
            </section>

            {/* 19. Changes to the Terms */}
            <section id="changes" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">19. Changes to the Terms</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  SnowExchange may update these Terms from time to time. Continued use after an update constitutes acceptance.
                </p>
              </div>
            </section>

            {/* 20. Contact */}
            <section id="contact" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">20. Contact</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
                  <p><strong>Legal & compliance:</strong> <a href="mailto:legal@snow-exchange.com" className="text-blue-600 hover:underline">legal@snow-exchange.com</a></p>
                  <p><strong>General support:</strong> <a href="mailto:support@snow-exchange.com" className="text-blue-600 hover:underline">support@snow-exchange.com</a></p>
                </div>
              </div>
            </section>

            {/* Annex A */}
            <section id="annex-a" className="mb-12 scroll-mt-24">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Annex A – Public Sector, Security & Residency Constraints</h2>
              <div className="space-y-6 text-gray-700 leading-relaxed">
                <div>
                  <h3 className="font-medium text-gray-800 mb-3">A1. Scope</h3>
                  <p>
                    This Annex applies to projects subject to regulatory, security, or sovereignty requirements, including public sector, defence, regulated industries, and sensitive infrastructure projects.
                  </p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-800 mb-3">A2. Possible constraints (non-exhaustive)</h3>
                  <p className="mb-2">Projects may require:</p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>residence in a specific country,</li>
                    <li>specific nationality,</li>
                    <li>security clearance or background checks,</li>
                    <li>mandatory on-site presence,</li>
                    <li>restrictions on remote work or offshoring,</li>
                    <li>enhanced confidentiality obligations.</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-medium text-gray-800 mb-3">A3. No verification by SnowExchange</h3>
                  <p>
                    SnowExchange does not verify eligibility, residency, nationality, permits, visas, or security clearances.
                  </p>
                </div>
                <div>
                  <h3 className="font-medium text-gray-800 mb-3">A4. User responsibility</h3>
                  <p>
                    Users are solely responsible for verifying eligibility and compliance and for addressing consequences of ineligibility, rejection, or project termination. SnowExchange disclaims all related liability to the maximum extent permitted by law.
                  </p>
                </div>
              </div>
            </section>

            {/* Related Documents */}
            <div className="border-t border-gray-200 pt-8 mt-12">
              <h3 className="font-semibold text-gray-800 mb-4">Related Documents</h3>
              <div className="flex flex-wrap gap-4">
                <Link to="/privacy" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors">
                  Privacy Policy
                </Link>
                <Link to="/ai-disclaimer" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 transition-colors">
                  AI Disclaimer
                </Link>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 pt-8 mt-8 text-center">
              <p className="text-sm text-gray-500">
                © {new Date().getFullYear()} SnowExchange. All rights reserved.
              </p>
              <p className="text-sm text-gray-400 mt-2">
                Last updated: {lastUpdated}
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TermsPage;
